import { type RootState } from "../../../redux/store";
import { createDeepEqualSelector } from "../../../Search/redux/createDeepEqualSelector";
import { selectFacets, selectTabularViewConfig } from "../../redux/selectors";
import { type TabularViewData } from "../../../Search/models";
import { shallowEqual, useSelector } from "react-redux";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { type HousingSearchForm } from "../../types/housingSAFormTypes";
import { type ColumnDef } from "@tanstack/react-table";
import {
  dataColSizeFn,
  tsmakeHSAActionsColumn,
  tsmakeHSAProductColumn,
  tsmakeTechValueColumnFactory,
} from "~/Search/components/SearchResults/TabularView/TanStackColumnDefs";
import { type AllScreenSizes, SIZE_DESKTOP_S } from "~/common/hooks/useNumericScreenSize";

const selectVisibleColumns = createDeepEqualSelector(selectTabularViewConfig, selectFacets, (columns, facetsByCode) =>
  columns.filter((column) => facetsByCode[column.techValueUID]?.values.length > 0)
);

const MAX_COL_AMOUNT = 99;

export const useHSATableConfig = (screenSize: AllScreenSizes) => {
  const unit = useFormContext<HousingSearchForm>().watch("dimensionUnit");
  const visibleColumns = useSelector(
    (state: RootState) => selectVisibleColumns(state, unit).map((column) => column.techValueUID),
    shallowEqual
  );
  const fullConfig = useSelector((state: RootState) => selectVisibleColumns(state, unit));

  const isDesktop = screenSize >= SIZE_DESKTOP_S;

  return useMemo(() => {
    const columnConfig: ColumnDef<TabularViewData>[] = [];
    const productColumn: ColumnDef<TabularViewData> = tsmakeHSAProductColumn(isDesktop);

    columnConfig.push(productColumn);

    const sizeCalculator = dataColSizeFn(isDesktop, MAX_COL_AMOUNT);
    const techValueColumnMaker = tsmakeTechValueColumnFactory(sizeCalculator);
    fullConfig.slice(0, MAX_COL_AMOUNT).forEach((col) => columnConfig.push(techValueColumnMaker(col)));
    columnConfig.push(tsmakeHSAActionsColumn());

    return columnConfig;
  }, [visibleColumns, screenSize]);
};
